/**
 * MCP Servers route — Model Context Protocol server management.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plug,
  Plus,
  Pencil,
  Trash2,
  Play,
  X,
  Loader2,
  FolderOpen,
  Search,
  Database,
  Server,
  Globe,
  Users,
  ChevronDown,
  ChevronRight,
  Power,
  PowerOff,
} from "lucide-react";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";
import { HexBadge } from "../components/ui/HexBadge";
import { StatusDot } from "../components/ui/StatusDot";

interface McpServer {
  id: string;
  name: string;
  displayName: string;
  description: string;
  transport: "stdio" | "http" | "sse";
  command: string | null;
  args: string[];
  env: Record<string, string>;
  url: string | null;
  enabled: boolean;
  icon: string;
  color: string | null;
  isBuiltin: boolean;
  lastStatus: "healthy" | "error" | "unknown";
  lastChecked: number | null;
  lastError: string | null;
}

interface McpServersResponse {
  servers: McpServer[];
}

interface AgentInfo {
  id: string;
  name: string;
  display_name: string;
}

// Icon map for MCP servers
const iconMap: Record<string, React.ElementType> = {
  FolderOpen,
  Search,
  Database,
  Server,
  Globe,
  Plug,
};

function McpIcon({ name, className, style }: { name: string; className?: string; style?: React.CSSProperties }) {
  const IconComponent = iconMap[name] ?? Plug;
  return <IconComponent className={className} style={style} />;
}

function McpServerCard({
  server,
  onTest,
  onEdit,
  onDelete,
  isExpanded,
  onToggleExpand,
}: {
  server: McpServer;
  onTest: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) {
  const queryClient = useQueryClient();

  // Fetch agents using this server (only when expanded)
  const { data: agentsData } = useQuery<{ agents: AgentInfo[] }>({
    queryKey: ["mcp-server-agents", server.id],
    queryFn: async () => {
      const res = await fetch(`/api/mcp/servers/${server.id}/agents`);
      return res.json();
    },
    enabled: isExpanded,
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/mcp/servers/${server.id}/test`, { method: "POST" });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcp-servers"] });
    },
  });

  // Toggle enabled/disabled. Works for both builtin and custom servers
  // (the backend's PUT /api/mcp/servers/:id allows flipping `enabled`
  // on builtins). Optimistic pattern via query invalidation.
  const toggleEnabledMutation = useMutation({
    mutationFn: async (nextEnabled: boolean) => {
      const res = await fetch(`/api/mcp/servers/${server.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: nextEnabled }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcp-servers"] });
    },
  });

  const statusColor =
    server.lastStatus === "healthy"
      ? "online"
      : server.lastStatus === "error"
        ? "error"
        : "warning";

  return (
    <CorpoCard
      className="relative cursor-pointer"
      accent
      style={{
        borderLeftColor: server.color ?? "var(--bw-accent)",
        borderLeftWidth: "3px",
        opacity: server.enabled ? 1 : 0.6,
      } as React.CSSProperties}
      onClick={onToggleExpand}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-12 h-12 flex items-center justify-center rounded-sm"
          style={{
            backgroundColor: `${server.color ?? "var(--bw-accent)"}20`,
            border: `1px solid ${server.color ?? "var(--bw-accent)"}`,
          }}
        >
          <McpIcon
            name={server.icon}
            className="w-6 h-6"
            style={{ color: server.color ?? "var(--bw-accent)" } as React.CSSProperties}
          />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3
              className="font-bold text-lg uppercase tracking-wide"
              style={{ color: server.color ?? "var(--bw-accent)" }}
            >
              {server.displayName}
            </h3>
            <StatusDot status={statusColor} size="sm" />
          </div>
          <p className="text-xs text-[var(--bw-muted)]">{server.transport.toUpperCase()}</p>
        </div>
        <div className="flex items-center">
          {isExpanded ? (
            <ChevronDown size={18} className="text-[var(--bw-muted)]" />
          ) : (
            <ChevronRight size={18} className="text-[var(--bw-muted)]" />
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-[var(--bw-fg)] mb-4 line-clamp-2">
        {server.description}
      </p>

      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        {!server.enabled && <HexBadge variant="warn">DISABLED</HexBadge>}
        {server.isBuiltin && <HexBadge variant="muted">BUILTIN</HexBadge>}
        {server.lastError && (
          <HexBadge variant="danger">
            ERROR
          </HexBadge>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
        <GlyphButton
          variant={server.enabled ? "ghost" : "primary"}
          size="sm"
          onClick={() => toggleEnabledMutation.mutate(!server.enabled)}
          disabled={toggleEnabledMutation.isPending}
          icon={
            toggleEnabledMutation.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : server.enabled ? (
              <PowerOff size={14} />
            ) : (
              <Power size={14} />
            )
          }
          title={server.enabled ? "Disable this server" : "Enable this server"}
        >
          {server.enabled ? "DISABLE" : "ENABLE"}
        </GlyphButton>
        <GlyphButton
          variant="secondary"
          size="sm"
          onClick={() => {
            onTest();
            testMutation.mutate();
          }}
          disabled={testMutation.isPending}
          icon={testMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
        >
          TEST
        </GlyphButton>
        <GlyphButton
          variant="ghost"
          size="sm"
          onClick={onEdit}
          disabled={server.isBuiltin}
          icon={<Pencil size={14} />}
          title={server.isBuiltin ? "Builtin core fields are read-only" : "Edit server"}
        >
          EDIT
        </GlyphButton>
        <GlyphButton
          variant="ghost"
          size="sm"
          onClick={onDelete}
          disabled={server.isBuiltin}
          icon={<Trash2 size={14} />}
          className={server.isBuiltin ? "" : "hover:text-[var(--bw-danger)]"}
          title={server.isBuiltin ? "Builtin servers cannot be deleted" : "Delete server"}
        >
          DELETE
        </GlyphButton>
      </div>

      {toggleEnabledMutation.error && (
        <p className="text-xs text-[var(--bw-danger)] mt-2">
          {(toggleEnabledMutation.error as Error).message}
        </p>
      )}

      {/* Expanded: Show agents using this server */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-[var(--bw-border)]">
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className="text-[var(--bw-muted)]" />
            <span className="text-xs text-[var(--bw-muted)] uppercase">Agents using this server</span>
          </div>
          {agentsData?.agents && agentsData.agents.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {agentsData.agents.map((agent) => (
                <HexBadge key={agent.id} variant="primary">
                  {agent.display_name}
                </HexBadge>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[var(--bw-muted)]">No agents assigned</p>
          )}

          {/* Show env vars (with secrets masked) */}
          {Object.keys(server.env).length > 0 && (
            <div className="mt-3">
              <span className="text-xs text-[var(--bw-muted)] uppercase">Environment</span>
              <div className="mt-1 font-mono text-xs bg-[var(--bw-surface)] p-2 rounded">
                {Object.entries(server.env).map(([key, value]) => (
                  <div key={key}>
                    <span className="text-[var(--bw-accent)]">{key}</span>=
                    <span className="text-[var(--bw-muted)]">
                      {value.startsWith("${secret:") ? "••••••••" : value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </CorpoCard>
  );
}

interface CreateMcpServerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateMcpServerModal({ isOpen, onClose, onCreated }: CreateMcpServerModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    description: "",
    transport: "stdio",
    command: "npx",
    args: "",
    url: "",
    icon: "Plug",
    color: "#00f0ff",
    enabled: true,
  });
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/mcp/servers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          args: formData.args.split("\n").filter((a) => a.trim()),
          env: {},
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? "Failed to create MCP server");
      }
      return res.json();
    },
    onSuccess: () => {
      onCreated();
      onClose();
      setFormData({
        name: "",
        display_name: "",
        description: "",
        transport: "stdio",
        command: "npx",
        args: "",
        url: "",
        icon: "Plug",
        color: "#00f0ff",
        enabled: true,
      });
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Unknown error");
    },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="w-full max-w-lg mx-4 bg-[var(--bw-bg)] border border-[var(--bw-border)] p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <CRTHeader text="Add MCP Server" level={3} />
          <button
            onClick={onClose}
            className="text-[var(--bw-muted)] hover:text-[var(--bw-fg)]"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-2 bg-[var(--bw-danger)]/10 border border-[var(--bw-danger)] text-[var(--bw-danger)] text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">Name (slug)</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })
                }
                placeholder="my-server"
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] font-mono focus:border-[var(--bw-accent)] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">Display Name</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="My Server"
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] focus:border-[var(--bw-accent)] focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-[var(--bw-muted)] mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What does this MCP server provide?"
              className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] focus:border-[var(--bw-accent)] focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">Transport</label>
              <select
                value={formData.transport}
                onChange={(e) => setFormData({ ...formData, transport: e.target.value })}
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] focus:border-[var(--bw-accent)] focus:outline-none"
              >
                <option value="stdio">STDIO</option>
                <option value="http">HTTP</option>
                <option value="sse">SSE</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">Command</label>
              <input
                type="text"
                value={formData.command}
                onChange={(e) => setFormData({ ...formData, command: e.target.value })}
                placeholder="npx"
                disabled={formData.transport !== "stdio"}
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] font-mono focus:border-[var(--bw-accent)] focus:outline-none disabled:opacity-50"
              />
            </div>
          </div>

          {formData.transport === "stdio" && (
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">Arguments (one per line)</label>
              <textarea
                value={formData.args}
                onChange={(e) => setFormData({ ...formData, args: e.target.value })}
                placeholder={"-y\n@modelcontextprotocol/server-example"}
                rows={3}
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] font-mono resize-none focus:border-[var(--bw-accent)] focus:outline-none"
              />
            </div>
          )}

          {formData.transport !== "stdio" && (
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">URL</label>
              <input
                type="text"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://example.com/mcp"
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] font-mono focus:border-[var(--bw-accent)] focus:outline-none"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">Icon</label>
              <select
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] focus:border-[var(--bw-accent)] focus:outline-none"
              >
                {Object.keys(iconMap).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">Color</label>
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-full h-[38px] bg-[var(--bw-surface)] border border-[var(--bw-border)] cursor-pointer"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <GlyphButton
            variant="primary"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !formData.name || !formData.display_name}
            className="flex-1"
          >
            {createMutation.isPending ? (
              <Loader2 className="animate-spin" size={14} />
            ) : (
              "CREATE SERVER"
            )}
          </GlyphButton>
          <GlyphButton variant="ghost" onClick={onClose}>
            CANCEL
          </GlyphButton>
        </div>
      </div>
    </div>
  );
}

export function McpRoute() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedServerId, setExpandedServerId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<McpServersResponse>({
    queryKey: ["mcp-servers"],
    queryFn: async () => {
      const res = await fetch("/api/mcp/servers");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/mcp/servers/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? "Failed to delete");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcp-servers"] });
    },
  });

  const handleDelete = (server: McpServer) => {
    if (server.isBuiltin) return;
    if (confirm(`Delete MCP server "${server.displayName}"?`)) {
      deleteMutation.mutate(server.id);
    }
  };

  const servers = data?.servers ?? [];

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Plug size={24} className="text-[var(--bw-accent)]" />
          <CRTHeader text="MCP Servers" level={2} />
        </div>
        <GlyphButton
          variant="secondary"
          onClick={() => setShowCreateModal(true)}
          icon={<Plus size={14} />}
        >
          ADD SERVER
        </GlyphButton>
      </div>

      <p className="text-sm text-[var(--bw-muted)]">
        Model Context Protocol servers provide external tools to Claude Code agents.
        Attach servers to agents in the Crew page to enable their capabilities.
      </p>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-[var(--bw-accent)]" size={32} />
        </div>
      )}

      {error && (
        <div className="p-4 bg-[var(--bw-danger)]/10 border border-[var(--bw-danger)] text-[var(--bw-danger)]">
          Error loading MCP servers
        </div>
      )}

      {!isLoading && !error && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {servers.map((server) => (
            <McpServerCard
              key={server.id}
              server={server}
              onTest={() => {}}
              onEdit={() => {}}
              onDelete={() => handleDelete(server)}
              isExpanded={expandedServerId === server.id}
              onToggleExpand={() =>
                setExpandedServerId(expandedServerId === server.id ? null : server.id)
              }
            />
          ))}

          {servers.length === 0 && (
            <div className="col-span-full text-center py-12 text-[var(--bw-muted)]">
              No MCP servers configured
            </div>
          )}
        </div>
      )}

      <CreateMcpServerModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ["mcp-servers"] })}
      />
    </div>
  );
}
